import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function ContactSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.08 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus("submitting");

    const form = e.target as HTMLFormElement;
    const formDataObj = new FormData(form);

    try {
      const response = await fetch("https://readdy.ai/api/form/d8mqg0080ubi47thlu2g", {
        method: "POST",
        body: new URLSearchParams(formDataObj as unknown as Record<string, string>),
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      if (response.ok) {
        setFormStatus("success");
        setFormData({ name: "", email: "", company: "", phone: "", message: "" });
        setTimeout(() => setFormStatus("idle"), 5000);
      } else {
        setFormStatus("error");
        setTimeout(() => setFormStatus("idle"), 5000);
      }
    } catch {
      setFormStatus("error");
      setTimeout(() => setFormStatus("idle"), 5000);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="py-24 md:py-32 bg-foreground-950"
    >
      <div className="px-6 lg:px-12 xl:px-20">
        <div className="reveal text-center mb-16 md:mb-20">
          <span className="text-xs font-semibold tracking-[0.2em] text-primary-400 uppercase mb-4 block">
            Contact
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-5 tracking-tight">
            {t("contact.title")}
          </h2>
          <p className="text-base md:text-lg text-white/40 max-w-2xl mx-auto leading-relaxed">
            {t("contact.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 max-w-5xl mx-auto">
          {/* Form */}
          <div className="reveal lg:col-span-3">
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                    {t("contact.name")} <span className="text-primary-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="w-full px-4 py-3 rounded-lg border border-white/10 bg-white/5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-primary-400/50 focus:ring-2 focus:ring-primary-400/10 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                    {t("contact.email")} <span className="text-primary-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@company.com"
                    className="w-full px-4 py-3 rounded-lg border border-white/10 bg-white/5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-primary-400/50 focus:ring-2 focus:ring-primary-400/10 transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                    {t("contact.company")}
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company Ltd."
                    className="w-full px-4 py-3 rounded-lg border border-white/10 bg-white/5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-primary-400/50 focus:ring-2 focus:ring-primary-400/10 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                    {t("contact.phone")}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 12345 67890"
                    className="w-full px-4 py-3 rounded-lg border border-white/10 bg-white/5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-primary-400/50 focus:ring-2 focus:ring-primary-400/10 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                  {t("contact.message")} <span className="text-primary-400">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="Describe your project, timeline, and requirements..."
                  className="w-full px-4 py-3 rounded-lg border border-white/10 bg-white/5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-primary-400/50 focus:ring-2 focus:ring-primary-400/10 transition-all resize-none"
                />
                <p className="text-xs text-white/20 mt-1.5 text-right">
                  {formData.message.length}/500
                </p>
              </div>

              <button
                type="submit"
                disabled={formStatus === "submitting"}
                className="w-full px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all duration-200 text-sm cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {formStatus === "submitting" ? (
                  <>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin" />
                    </div>
                    Sending...
                  </>
                ) : (
                  <>
                    {t("contact.submit")}
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </div>
                  </>
                )}
              </button>

              {formStatus === "success" && (
                <div className="p-4 bg-accent-500/15 border border-accent-500/30 rounded-lg text-sm text-accent-300 flex items-center gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line" />
                  </div>
                  Thank you. Your inquiry has been received. We'll respond within one business day.
                </div>
              )}

              {formStatus === "error" && (
                <div className="p-4 bg-primary-500/10 border border-primary-500/20 rounded-lg text-sm text-primary-300 flex items-center gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-error-warning-line" />
                  </div>
                  Something went wrong. Please try again or email us directly.
                </div>
              )}
            </form>
          </div>

          {/* Contact Info */}
          <div className="reveal lg:col-span-2 space-y-8" style={{ transitionDelay: "200ms" }}>
            <div>
              <h3 className="text-lg font-bold text-white mb-1 tracking-tight">
                Our Office
              </h3>
              <p className="text-sm text-white/40 leading-relaxed">
                We operate globally from our headquarters in Noida, India.
              </p>
            </div>

            <div className="space-y-5">
              <div className="flex items-start gap-3.5">
                <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-line text-white/60 text-sm" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-1">Address</p>
                  <p className="text-sm text-white/70 leading-relaxed">
                    C Tower, 10th Floor<br />
                    Noida Sector 62<br />
                    Uttar Pradesh 201309, India
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3.5">
                <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line text-white/60 text-sm" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-1">Phone</p>
                  <p className="text-sm text-white/70">+91 6262623452</p>
                </div>
              </div>

              <div className="flex items-start gap-3.5">
                <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-line text-white/60 text-sm" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-1">Email</p>
                  <p className="text-sm text-white/70">emporris.solutions@outlook.com</p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-xl overflow-hidden border border-white/10 h-44 md:h-52">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.123456!2d77.3647!3d28.6139!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDM2JzUwLjAiTiA3N8KwMjEnNTIuOSJF!5e0!3m2!1sen!2sin!4v1600000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Emporris Office Location"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}